// Investor snapshot publish workflow — per-company AND per-period.
//
// Each reporting period (e.g. 'jan-2026', 'dec-2025') has its own publish
// state so an admin can publish January without unpublishing December, and
// unpublishing a period rolls back to that period's previous snapshot only.
//
// Storage keys:
//   finkloz_investor_snapshot_{companyId}_{period}          → current
//   finkloz_investor_snapshot_history_{companyId}_{period}  → history (cap 20)
//
// When Supabase is wired up, replace the storage layer with a table
// `investor_snapshots (company_id, period, published_at, published_by, data jsonb)`
// and keep the same public API.

const STORAGE_PREFIX = 'finkloz_investor_snapshot_';
const HISTORY_PREFIX = 'finkloz_investor_snapshot_history_';

function scope(companyId, period) {
  return `${companyId || 'default'}_${period || 'default'}`;
}
function key(companyId, period) {
  return STORAGE_PREFIX + scope(companyId, period);
}
function historyKey(companyId, period) {
  return HISTORY_PREFIX + scope(companyId, period);
}

export function getPublishedSnapshot(companyId, period) {
  try {
    const raw = localStorage.getItem(key(companyId, period));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function publishSnapshot(companyId, period, publishedBy, data) {
  const snapshot = {
    period: period || null,
    publishedAt: new Date().toISOString(),
    publishedBy: publishedBy || 'unknown',
    data: data || {},
  };
  try {
    localStorage.setItem(key(companyId, period), JSON.stringify(snapshot));
    const histRaw = localStorage.getItem(historyKey(companyId, period));
    const hist = histRaw ? JSON.parse(histRaw) : [];
    hist.unshift(snapshot);
    localStorage.setItem(historyKey(companyId, period), JSON.stringify(hist.slice(0, 20)));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('publishSnapshot failed', e);
  }
  return snapshot;
}

// Unpublish this period's current snapshot. Rolls back to the previous
// entry in this period's history (if any); otherwise clears it.
export function unpublishSnapshot(companyId, period) {
  try {
    const histRaw = localStorage.getItem(historyKey(companyId, period));
    const hist = histRaw ? JSON.parse(histRaw) : [];
    const dropped = hist.shift();
    localStorage.setItem(historyKey(companyId, period), JSON.stringify(hist));
    const next = hist[0] || null;
    if (next && next.data) {
      localStorage.setItem(key(companyId, period), JSON.stringify(next));
    } else {
      localStorage.removeItem(key(companyId, period));
    }
    return { dropped, current: next };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('unpublishSnapshot failed', e);
    return { dropped: null, current: null };
  }
}

export function getPublishHistory(companyId, period) {
  try {
    const raw = localStorage.getItem(historyKey(companyId, period));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function buildSnapshotPayload({ liquidity, burn, runwayMonths, totalHC, period, risks, alerts, baseCurrency, fxRate }) {
  return {
    period: period || null,
    liquidity: liquidity ?? null,
    burn: burn ?? null,
    runwayMonths: runwayMonths ?? null,
    headcount: totalHC ?? null,
    risks: risks || [],
    alerts: alerts || [],
    // FX snapshot: record the base currency + rate at publish time so
    // historical reports always convert using the rate from when they
    // were published, not today's rate.
    baseCurrency: baseCurrency || 'CAD',
    publishFxRate: typeof fxRate === 'number' ? fxRate : 1,
  };
}
